import React from 'react';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { formatHumanScanWindow } from '../../domain/acceptance/scanVerifyNavigation';
import type { CaseScanRegisterRow } from '../../domain/safetyCases/scanSigningPresenter';
import { presentCaseScanRegister } from '../../domain/safetyCases/scanSigningPresenter';
import type { CaseTimelineEvent } from '../../services/reportsApi';

type CaseScanRegisterProps = {
  rows: CaseScanRegisterRow[];
  currentScanId?: string | null;
  latestSignedId?: string | null;
  siteTimeZone?: string | null;
  onOpenScan: (reportId: string) => void;
  onViewSignedRecord?: (reportId: string) => void;
};

function scanStateLabel(row: CaseScanRegisterRow): string {
  if (!row.unsigned) return 'Signed';
  if (row.status === 'draft') return 'Draft';
  return row.status ? row.status.replace(/_/g, ' ') : 'Scan';
}

export const CaseScanRegister: React.FC<CaseScanRegisterProps> = ({
  rows,
  currentScanId,
  latestSignedId,
  siteTimeZone,
  onOpenScan,
  onViewSignedRecord,
}) => {
  const currentRow = rows.find((row) => row.reportId === currentScanId) || null;
  const latestSignedRow = rows.find((row) => row.reportId === latestSignedId) || null;
  return (
    <Stack spacing={1.25} data-testid="safety-case-scan-register">
      <Typography variant="body2" color="text.secondary" data-testid="safety-case-scan-register-copy">
        Open a specific Safety Scan. Signing is a governance boundary for that Scan only — not every daily Scan needs a Signed Record.
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="safety-case-current-scan"
        data-report-id={currentScanId || ''}
      >
        Current Scan: {currentRow
          ? formatHumanScanWindow(currentRow.windowStart, currentRow.windowEnd, siteTimeZone)
          : (currentScanId ? 'Open Current Scan' : 'None')}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="safety-case-latest-signed"
        data-report-id={latestSignedId || ''}
      >
        Latest Signed: {latestSignedRow
          ? formatHumanScanWindow(latestSignedRow.windowStart, latestSignedRow.windowEnd, siteTimeZone)
          : (latestSignedId ? 'View Signed Record' : 'None')}
      </Typography>
      {rows.length ? rows.map((row) => (
        <Box
          key={row.reportId}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25 }}
          data-testid={`safety-case-scan-row-${row.reportId}`}
          data-report-id={row.reportId}
        >
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} data-testid={`safety-case-scan-window-${row.reportId}`}>
              {formatHumanScanWindow(row.windowStart, row.windowEnd, siteTimeZone)}
            </Typography>
            <Chip size="small" label={scanStateLabel(row)} />
            {row.relation ? (
              <Chip
                size="small"
                variant={row.relation === 'CROSSING' ? 'outlined' : 'filled'}
                color={row.relation === 'CROSSING' ? 'warning' : 'default'}
                label={row.relation}
                data-testid={`safety-case-scan-relation-${row.reportId}`}
              />
            ) : null}
            <Chip size="small" label={row.unsigned ? 'Unsigned' : 'Signed Record'} />
            {row.isCurrentScan ? <Chip size="small" color="primary" label="Current Scan" /> : null}
            {row.isLatestSigned ? <Chip size="small" color="success" label="Latest Signed" /> : null}
          </Stack>
          <details>
            <summary>
              <Typography component="span" variant="caption" color="text.secondary">Technical identity</Typography>
            </summary>
            <Typography variant="caption" color="text.secondary" data-testid={`safety-case-scan-id-${row.reportId}`}>
              {row.reportId}
            </Typography>
          </details>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => onOpenScan(row.reportId)}
              data-testid={`safety-case-open-scan-${row.reportId}`}
            >
              Open this Safety Scan
            </Button>
            {!row.unsigned && onViewSignedRecord ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onViewSignedRecord(row.reportId)}
                data-testid={`safety-case-view-signed-${row.reportId}`}
              >
                View Signed Record
              </Button>
            ) : null}
          </Stack>
        </Box>
      )) : (
        <Typography variant="body2" color="text.secondary">No Safety Scans are recorded on this Case yet.</Typography>
      )}
    </Stack>
  );
};

type CaseVerifyPanelProps = {
  events?: CaseTimelineEvent[] | null;
  currentScanId?: string | null;
  latestSignedId?: string | null;
  siteTimeZone?: string | null;
  currentScanPendingCount?: number | null;
  controlRevisionId?: string | null;
  implementedAt?: string | null;
  onOpenScan: (reportId: string) => void;
  onOpenCurrentScan?: () => void;
  onViewSignedRecord?: (reportId: string) => void;
};

export const CaseVerifyPanel: React.FC<CaseVerifyPanelProps> = ({
  events,
  currentScanId,
  latestSignedId,
  siteTimeZone,
  currentScanPendingCount,
  controlRevisionId,
  implementedAt,
  onOpenScan,
  onOpenCurrentScan,
  onViewSignedRecord,
}) => (
  <Card variant="outlined" sx={{ borderRadius: 3 }} data-testid="safety-case-verify-panel">
    <CardContent>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Verify observations</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select a specific Safety Scan. Signing applies to that Scan only.
      </Typography>
      <CaseScanRegister
        rows={presentCaseScanRegister({
          events,
          currentScanReportId: currentScanId,
          latestSignedReportId: latestSignedId,
          controlRevisionId,
          implementedAt,
        })}
        currentScanId={currentScanId}
        latestSignedId={latestSignedId}
        siteTimeZone={siteTimeZone}
        onOpenScan={onOpenScan}
        onViewSignedRecord={onViewSignedRecord}
      />
      {currentScanId ? (
        <Button
          variant="contained"
          color="warning"
          onClick={onOpenCurrentScan}
          data-testid="safety-case-verify-link"
          sx={{ fontWeight: 800, mt: 2 }}
        >
          {currentScanPendingCount != null && currentScanPendingCount > 0
            ? `Verify observations — ${currentScanPendingCount} pending`
            : 'Open Current Scan'}
        </Button>
      ) : null}
    </CardContent>
  </Card>
);
